/* eslint-disable prettier/prettier */
/* eslint-disable import/no-duplicates */
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { format, parseISO } from 'date-fns';

import { Container, Button, Row, Col } from 'react-bootstrap';

import api from '../../../services/api';

import { TIPO_USUARIO } from '../../../utils/funcoes';

import { useToast } from '../../../hooks/toast';
import { useAuth } from '../../../hooks/auth';
import { useClinica } from '../../../hooks/clinica';

import Input from '../../../components/Input';
import SelectSync from '../../../components/SelectSync';
import Loading from '../../../components/Loading';

import getValidationErrors from '../../../utils/getValidationErrors';

import getResponseErrors from '../../../utils/getResponseErrors';
import PageInfo from '../../../components/PageInfo';
import HeaderRelatorio from '../HeaderRelatorio';
import ExibeDadosTabela from './ExibeDadosTabela';

export interface IDadosRetornoAgendamentosDiaItem {
  item: string;
  total: number;
}

interface IRelatorio {
  porAgenda: IDadosRetornoAgendamentosDiaItem[];
  porUsuario: IDadosRetornoAgendamentosDiaItem[];
  site: IDadosRetornoAgendamentosDiaItem[];
  whatsapp: IDadosRetornoAgendamentosDiaItem[];
}

interface IFormBusca {
  dataInicio: Date;
  dataFim: Date;
  CliCodigo: number;
  somenteValidos: string;
}

interface IDataExibe {
  dataHora: string;
  dataDiaSemana: string;
}

const optionsTipo = [
  { value: 'false', label: 'Todos' },
  { value: 'true', label: 'Só Ativos' },
];

const AgendamentosRealizadosDia: React.FC = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { user } = useAuth();
  const { optionsClinica } = useClinica();

  const formRefBusca = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [dadosRelatorio, setDadosRelatorio] = useState<IRelatorio>();

  const initialData = {
    dataInicio: format(new Date(), 'yyyy-MM-dd'),
    dataFim: format(new Date(), 'yyyy-MM-dd'),
    CliCodigo: user.UsuClinica.toString(),
    somenteValidos: 'false',
  };
  const [dataExibe, setDataExibe] = useState<IDataExibe>();

  useEffect(() => {
    if (user.UsuTipo > TIPO_USUARIO.SUPERVISOR) {
      history.push('/sempermissao');
    }
  }, [history, user.UsuTipo]);

  const informacoesPagina = `<b>Descrição:</b> Exibe a quantidade total de agendamentos realizados em um determinado período, agrupando por usuário e por agenda.`;

  const handleSubmitBusca = useCallback(
    async (dados: IFormBusca) => {
      setLoading(true);
      try {
        setDadosRelatorio(undefined);
        formRefBusca.current?.setErrors({});
        const schema = Yup.object().shape({
          dataInicio: Yup.date().required(),
          dataFim: Yup.date().required(),
          CliCodigo: Yup.string().nullable(true),
          somenteValidos: Yup.boolean().nullable(true),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        const dataInicioSelecionada = parseISO(dados.dataInicio.toString());
        const dataFimSelecionada = parseISO(dados.dataFim.toString());

        const dataInicioFormatada = format(dataInicioSelecionada, 'yyyy-MM-dd');
        const dataFimFormatada = format(dataFimSelecionada, 'yyyy-MM-dd');

        setDataExibe({
          dataDiaSemana: `${format(
            dataInicioSelecionada,
            'dd/MM/yyyy',
          )} até ${format(dataFimSelecionada, 'dd/MM/yyyy')}`,
          dataHora: format(new Date(), 'dd/MM/yyyy HH:mm:ss'),
        });

        let parametrosOpcionais = dados.CliCodigo
          ? `&CliCodigo=${dados.CliCodigo}`
          : '';
        parametrosOpcionais = dados.somenteValidos
          ? `&somenteValidos=${dados.somenteValidos}`
          : '';

        const response = await api.get<IRelatorio>(
          `/relatorios/agendamentosrealizadosdia?dataInicio=${dataInicioFormatada}&dataFim=${dataFimFormatada}${parametrosOpcionais}`,
        );

        setDadosRelatorio(response.data);
      } catch (error) {
        console.log(error);
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefBusca.current?.setErrors(errors);
        } else {
          const mensagem = getResponseErrors(error);
          addToast({
            type: 'error',
            title: 'Não foi possível carregar dados',
            description: mensagem,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChangeVazio = useCallback(() => { }, []);

  return (
    <Container fluid className="mt-2">
      {loading && <Loading />}
      <>
        <div className="d-print-none bg-white border p-2 mb-2">
          <div className="p-2 mb-2 d-flex justify-content-between">
            <h2 id="topoBusca">Agendamentos Realizados</h2>
            <PageInfo
              descricao={informacoesPagina}
              permissao={TIPO_USUARIO.SUPERVISOR}
            />
          </div>
          {user && (
            <Form
              ref={formRefBusca}
              initialData={initialData}
              onSubmit={handleSubmitBusca}
            >
              <Row>
                <Col md={2}>
                  <Input name="dataInicio" label="De" type="date" required />
                </Col>
                <Col md={2}>
                  <Input name="dataFim" label="Até" type="date" required />
                </Col>
                <Col md={2}>
                  <SelectSync
                    name="somenteValidos"
                    label="Tipo"
                    options={optionsTipo}
                    isClearable
                    handleSelectChange={handleOnChangeVazio}
                  />
                </Col>
                <Col md={2}>
                  <SelectSync
                    name="CliCodigo"
                    label="Clínica"
                    options={optionsClinica}
                    isClearable
                    handleSelectChange={handleOnChangeVazio}
                  />
                </Col>
                <Col md={1}>
                  <br />
                  <Button type="submit" size="sm" variant="outline-secondary">
                    BUSCAR
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
        </div>

        <HeaderRelatorio
          dataImpressao={dataExibe?.dataHora}
          titulo={`REGISTRO AGENDAMENTOS ${dataExibe?.dataDiaSemana || ''}`}
        />

        <ExibeDadosTabela
          titulo="Agendamentos Por Usuário"
          tituloItem="Usuário"
          dados={dadosRelatorio?.porUsuario}
        />

        <ExibeDadosTabela
          titulo="Agendamentos Por Agenda"
          tituloItem="Agenda"
          dados={dadosRelatorio?.porAgenda}
        />

        <ExibeDadosTabela
          titulo="Agendamentos Site"
          tituloItem="Agenda"
          dados={dadosRelatorio?.site}
        />

        <ExibeDadosTabela
          titulo="Agendamentos Whatsapp"
          tituloItem="Agenda"
          dados={dadosRelatorio?.whatsapp}
        />
      </>
    </Container>
  );
};

export default AgendamentosRealizadosDia;
